import { ButtonBase, Skeleton } from "@mui/material";
import { classes } from "portal/theme/theme";
import {
  DEFAULT_AVERAGE_PPCM,
  Dimensions,
  getPlantAwareCropping,
} from "portal/utils/categoryCollection/image";
import { DiffOverlay } from "../imageOverlays/DiffOverlay";
import { OrderedCategorizedImage } from "./useCategorizedImageData";
import { PaddingDerivedPointLabelOverlay } from "../imageOverlays/PointLabelOverlay";
import { PredictionPoint } from "protos/veselka/prediction_point";
import {
  RemoveImageOverlay,
  UploadedByOperatorImageOverlay,
} from "../imageOverlays/IconOverlay";
import { StringDiff } from "portal/utils/strings";
import {
  ThumbnailImage,
  ThumbnailImageItem,
  ThumbnailWrapper,
} from "portal/components/images/ThumbnailImage";
import React, { FC, ReactNode, useMemo } from "react";

interface CategorizedThumbnailsProps {
  dimensions: Dimensions;
  images: OrderedCategorizedImage[];
  showLabels: boolean;
  onRemove?: (predictionPointId: string) => void;
  onDoubleClick?: (index: number, data: PredictionPoint) => void;
  onRightClick?: (
    predictionPointId: string,
    position: { x: number; y: number }
  ) => void;
  onClick?: (predictionPointId: string) => void;
  disabledChipIds?: ReadonlySet<string>;
  renderChipOverlay?: (predictionPointId: string) => ReactNode;
  diff?: StringDiff;
}

export const CategorizedThumbnails: FC<CategorizedThumbnailsProps> = ({
  images,
  dimensions,
  showLabels,
  onRemove,
  onDoubleClick,
  onRightClick,
  onClick,
  disabledChipIds,
  renderChipOverlay,
  diff,
}) => {
  const diffAddedSet = useMemo(
    () => (diff ? new Set(diff.added) : undefined),
    [diff]
  );
  const diffRemovedSet = useMemo(
    () => (diff ? new Set(diff.removed) : undefined),
    [diff]
  );
  return images.map(({ id, data, loading }, i) => {
    const disabled = Boolean(id && disabledChipIds?.has(id));
    const canToggle = Boolean(id && onClick && !disabled);
    const key = id ?? (loading ? `loading-${i}` : `broken-${i}`);
    const contentClassName = classes("h-full w-full", { grayscale: disabled });
    const toggle = id && onClick ? () => onClick(id) : undefined;

    if (loading) {
      return (
        <ThumbnailWrapper
          dimensions={dimensions}
          className={renderChipOverlay ? "group" : undefined}
          key={key}
        >
          <ClickabilityWrapper
            className={contentClassName}
            clickable={canToggle}
            onClick={toggle}
          >
            <Skeleton
              variant="rectangular"
              width={dimensions.width}
              height={dimensions.height}
            />
          </ClickabilityWrapper>
          {id && renderChipOverlay?.(id)}
        </ThumbnailWrapper>
      );
    }

    // show the saved or unsaved thumbnail (or a broken icon if image is undefined)
    let renderedThumbnail: ThumbnailImageItem | undefined;
    if (data && data.image) {
      const {
        x,
        y,
        radius,
        id,
        image: { url, ppcm },
      } = data;
      const {
        x: croppedX,
        y: croppedY,
        width: croppedWith,
        height: croppedHeight,
        paddingFactor,
      } = getPlantAwareCropping({
        x,
        y,
        radius,
      });
      const thumbnail = {
        id,
        url,
        x: croppedX,
        y: croppedY,
        width: croppedWith,
        height: croppedHeight,
        radius,
        ppcm,
      };
      renderedThumbnail = {
        ...thumbnail,
        onDoubleClick: onDoubleClick
          ? () => {
              onDoubleClick(i, data);
            }
          : undefined,
        onRightClick: onRightClick
          ? (e: React.MouseEvent) => {
              onRightClick(id, { x: e.clientX, y: e.clientY });
            }
          : undefined,
        onClick:
          canToggle && onClick
            ? () => {
                onClick(id);
              }
            : undefined,
        renderOverlay: () => (
          <>
            {diffAddedSet?.has(id) && (
              <DiffOverlay dimensions={dimensions} change="added" />
            )}
            {diffRemovedSet?.has(id) && (
              <DiffOverlay dimensions={dimensions} change="removed" />
            )}
            {showLabels && (
              <PaddingDerivedPointLabelOverlay
                padding={dimensions.width * paddingFactor}
                radiusCm={
                  thumbnail.radius /
                  (thumbnail.ppcm > 0 ? thumbnail.ppcm : DEFAULT_AVERAGE_PPCM)
                }
                dimensions={dimensions}
              />
            )}
            {data.image?.uploadedByOperator && (
              <UploadedByOperatorImageOverlay />
            )}
            {onRemove && (
              <RemoveImageOverlay
                onClick={() => {
                  onRemove(data.id);
                }}
              />
            )}
          </>
        ),
      };
    }

    const renderErrorOverlay = (): ReactNode => (
      <>
        {id && diffAddedSet?.has(id) && (
          <DiffOverlay dimensions={dimensions} change="added" />
        )}
        {id && diffRemovedSet?.has(id) && (
          <DiffOverlay dimensions={dimensions} change="removed" />
        )}
        {onRemove && id && (
          <RemoveImageOverlay
            onClick={() => {
              onRemove(id);
            }}
          />
        )}
      </>
    );
    return (
      <ThumbnailWrapper
        dimensions={dimensions}
        className={renderChipOverlay ? "group" : undefined}
        key={key}
      >
        <ClickabilityWrapper
          className={contentClassName}
          clickable={canToggle && !renderedThumbnail}
          onClick={toggle}
        >
          <ThumbnailImage
            className={canToggle ? "cursor-pointer" : "cursor-auto"}
            image={renderedThumbnail}
            renderErrorOverlay={renderErrorOverlay}
          />
        </ClickabilityWrapper>
        {id && renderChipOverlay?.(id)}
      </ThumbnailWrapper>
    );
  });
};

interface SuggestedPlaceholders {
  count: number;
  dimensions: Dimensions;
}
export const SuggestedPlaceholders: FC<SuggestedPlaceholders> = ({
  count,
  dimensions,
}) =>
  Array.from({ length: count })
    .fill(undefined)
    .map((_, i) => (
      <ThumbnailWrapper dimensions={dimensions} key={`suggested-addition-${i}`}>
        <div className="border-dashed border-gray-400" style={dimensions} />
      </ThumbnailWrapper>
    ));

interface ClickabilityWrapperProps {
  className: string;
  clickable: boolean;
  onClick?: () => void;
  children: ReactNode;
}

const ClickabilityWrapper: FC<ClickabilityWrapperProps> = ({
  className,
  clickable,
  onClick,
  children,
}) => {
  if (clickable && onClick) {
    return (
      <ButtonBase className={classes(className, "p-0")} onClick={onClick}>
        {children}
      </ButtonBase>
    );
  }
  return <div className={className}>{children}</div>;
};
